import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import eyeson from 'eyeson';

import classes from './GuestVideoConsulting.module.scss';
import callIcon from '../../assets/icons/call-icon.svg';
import microphoneIcon from '../../assets/icons/microphoneOn.svg';
import microphoneIconMuted from '../../assets/icons/microphoneOff.svg';
import videoIcon from '../../assets/icons/cameraOn.svg';
import videoIconStopped from '../../assets/icons/cameraOff.svg';
import Spinner from '../../components/UI/Spinner/Spinner';

import ecocareLogo from '../../assets/images/ecocare-logo.svg';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import { formatTime } from '../../utils/miscUtils';
import { fetchDoctors, IDoctor, selectDoctors } from '../../store/slices/doctors';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { useTranslation } from '../../hooks/useTranslations';

const VideoConsultingRoom: React.FC<any> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [pageInfo] = useState<any>(location.state);
  const [isLoadingEyeson, setIsLoadingEyeson] = useState<boolean>(true);
  let video: any = useRef<HTMLVideoElement>();
  let videoLocal: any = useRef<HTMLVideoElement>();
  const [skip, setSkip] = useState<boolean>(true);
  const [hasAudio, setHasAudio] = useState<boolean>(true);
  const [hasVideo, setHasVideo] = useState<boolean>(true);
  const [hasCallEnded, setHasCallEnded] = useState<boolean>(false);
  const { translations } = useTranslation();
  const [infoPartExtended] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);

  const doctorAssignedFullName = useMemo(() => {
    const tempoDoctorAssigned = doctors.find((doctor) =>
      doctor.person_appointments.find((appointment) => {
        if (
          pageInfo?.salesOrder?.sales_order_item?.sales_order_id &&
          pageInfo?.salesOrder?.sales_order_item?.sales_order_item_id
        ) {
          return (
            appointment.sales_order_id == pageInfo?.salesOrder?.sales_order_item.sales_order_id &&
            appointment.sales_order_item_id ==
              pageInfo?.salesOrder?.sales_order_item.sales_order_item_id
          );
        }
      })
    );
    if (tempoDoctorAssigned) {
      return `${tempoDoctorAssigned.person_firstname} ${tempoDoctorAssigned.person_lastname}`;
    }
    return undefined;
  }, [doctors, pageInfo?.salesOrder]);

  useEffect(() => {
    video = document.querySelector('#videostream');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');

    videoLocal = document.querySelector('#videolocal');
    videoLocal.setAttribute('autoplay', '');
    videoLocal.setAttribute('muted', '');
    videoLocal.setAttribute('playsinline', '');

    createRoomApiCall();
    const interval = setInterval(() => setTimer((timer) => timer + 1), 1000);

    return function cleanup() {
      eyeson.destroy();
      if (videoLocal && videoLocal.srcObject) {
        videoLocal.srcObject.getTracks().forEach((track: any) => track.stop());
      }
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!skip) {
      eyeson.send({
        type: 'change_stream',
        audio: hasAudio,
        video: hasVideo,
      });
    }
  }, [hasAudio, hasVideo, skip]);

  useEffect(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  const createRoomApiCall = async () => {
    if (pageInfo?.salesOrder?.work_order?.work_order_status_id === 2) {
      setIsLoadingEyeson(false);
      setHasCallEnded(true);
      return;
    }
    eyeson.onEvent((event: any) => handleEvent(event));
    try {
      let stream: MediaStream;
      stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      video.srcObject = stream;
      video.play();
      videoLocal.muted = true;
      videoLocal.srcObject = stream;
      videoLocal.setAttribute('style', 'display:none');
      videoLocal.play();
      eyeson.send({
        type: 'request_guest_user',
        api: 'https://api.eyeson.team',
        token: pageInfo?.guestToken,
        name: pageInfo?.salesOrder.sales_order?.customer_person?.person_firstname,
      });
    } catch (_) {
      setIsLoadingEyeson(false);
    }
  };

  const handleEvent = (event: any) => {
    if (!event) {
      return;
    }
    let videoElement: any = document.getElementById('videostream');
    switch (event.type) {
      case 'connection': {
        if (event.connectionStatus === 'ready' || event.connectionStatus === 'connected') {
          eyeson.join({ audio: true, video: true, stream: videoElement.srcObject });
        } else if (event.connectionStatus === 'access_denied') {
          setIsLoadingEyeson(false);
          toast.error(translations('placeholder.access.key'), {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          quitSession();
          let videoLocal2: any = document.querySelector('#videolocal');
          if (videoLocal2 && videoLocal2.srcObject) {
            videoLocal2.srcObject.getTracks().forEach((track: any) => track.stop());
          }
        }
        break;
      }
      case 'accept': {
        videoElement.srcObject = event.remoteStream;
        videoElement.play();
        setTimer(
          Math.floor((new Date().getTime() - new Date(eyeson?.room?.started_at).getTime()) / 1000)
        );
        setIsLoadingEyeson(false);
        break;
      }
      case 'add_user': {
        if (event.user.guest) {
          videoLocal.setAttribute('style', 'display:inline');
          videoElement.setAttribute('style', 'display:inline');
          video.setAttribute('style', 'display:inline');
        }
        break;
      }
      case 'guest_user': {
        if (event.token) eyeson.connect(event.token);
        break;
      }
      case 'remove_user': {
        videoLocal.setAttribute('style', 'display:none');
        setHasCallEnded(true);
        break;
      }
      default:
        break;
    }
  };

  const quitSession = () => {
    eyeson.destroy();
    setHasCallEnded(true);
    navigate('/web/schedule');
  };

  const editStream = (event: any) => {
    setSkip(false);
    event.target.alt === 'microphoneIcon'
      ? setHasAudio((prevHasAudio) => !prevHasAudio)
      : setHasVideo((prevHasVideo) => !prevHasVideo);
  };

  return (
    <div className={`${classes.container}`}>
      {!hasCallEnded ? (
        <div
          className={`${classes.videoContainer}`}
          style={{
            flex: 1,
          }}
        >
          <div className="video">
            <video
              id="videolocal"
              className={classes.localVideo}
              playsInline
              autoPlay
              muted
            ></video>
            {!hasVideo && <div className={classes.localVideoHide}></div>}
            <video id="videostream" className={classes.videoStream}></video>
          </div>
          <div className={classes.videoControlsContainer}>
            <div className={classes.timeStamp}>{formatTime(timer)}</div>
            <div className={classes.videoControlsMid}>
              <img
                src={hasAudio ? microphoneIcon : microphoneIconMuted}
                className={classes.callIcon}
                alt="microphoneIcon"
                onClick={(e) => editStream(e)}
              />
              <img
                src={callIcon}
                className={classes.callIcon}
                alt="callIcon"
                onClick={quitSession}
              />
              <img
                src={hasVideo ? videoIcon : videoIconStopped}
                className={classes.callIcon}
                alt="videoIcon"
                onClick={(e) => editStream(e)}
              />
            </div>
            <div className={classes.videoControlsRight}>
              {/* <img
                src={callOptionsIcon}
                className={classes.callIcon}
                alt="callOptions"
                onClick={() => {
                  setShowPatientDetails((prevShowPatientDetails) => !prevShowPatientDetails);
                }}
              /> */}
            </div>
          </div>
          {isLoadingEyeson && (
            <div className={classes.loadingVideo}>
              <Spinner />
            </div>
          )}
        </div>
      ) : (
        !infoPartExtended && (
          <div
            className={classes.videoEndContainer}
            style={{
              flex: infoPartExtended ? 0 : 1,
              justifyContent: 'center',
            }}
          >
            <div>
              <img src={ecocareLogo} alt="ecocareLogo" />
              <div className={classes.callInfoContainer}>
                <div className={classes.callInfoLabel}>{translations('video.call with')}</div>
                <div className={classes.patientFullName}>{doctorAssignedFullName}</div>
                <div className={classes.callInfoLabel}>{translations('has.ended')}</div>
              </div>
              <PrimaryButtonV2
                text={translations('close')}
                buttonStyle={{ height: 50, minWidth: 213 }}
                containerStyle={{ marginTop: 30 }}
                onClick={() => navigate('/web/schedule')}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default VideoConsultingRoom;
